import { useEffect, useRef, useState } from "react";

import { Button, Grid2, Typography } from "@mui/material";
// import { FaCheck } from "react-icons/fa6";
import { Link, useParams } from "react-router-dom";

import { TCourse, TProfile } from "../../../@types";
import { apis, callApi } from "../../../api";
import { useAccount } from "../../../hooks/useAccount";
import { useWhiteScreen } from "../../../hooks/useWhiteScreen";
import { Container } from "../../common/Container/Container";
import {
	CourseList,
	TSplittedCourses,
} from "../../common/CourseList/CourseList";
import {
	useCourseCategories,
	useCourseCategoriesInit,
} from "../../../hooks/useCourseCategories";
import {
	ImageViewer,
	TImageViewerRef,
} from "../../common/FileViewer/ImageViewer";
import {
	TVideoViewerRef,
	VideoViewer,
} from "../../common/FileViewer/VideoViewer";
import { Loading } from "../../common/Loading/Loading";
import { NotFound } from "../../common/NotFound/NotFound";
import { RichTypography } from "../../common/RichTypography/RichTypography";
import noAvatarImage from "../../imagenes/no_avatar.svg";

import "./public_profile.scss";

export function PublicProfile() {
	const { account } = useAccount();
	useWhiteScreen();
	type TState = {
		currentProfile: TProfile | null;
		loading: boolean;
		courses: TCourse[];
	};
	const [state, setState] = useState<TState>({
		currentProfile: null,
		loading: true,
		courses: [],
	});
	const params = useParams();
	const profileSlug = params.profileSlug ?? "";

	const imageViewerRef = useRef<TImageViewerRef>(null);
	const videoViewerRef = useRef<TVideoViewerRef>(null);

	const { categories } = useCourseCategories();
	useCourseCategoriesInit();

	useEffect(() => {
		const getProfile = async () => {
			try {
				if (!profileSlug) {
					return;
				}

				const response = await callApi({
					method: "GET",
					url: `${apis.PUBLIC_PROFILE}/${profileSlug}`,
				});

				if (!response) {
					return;
				}

				setState((prevState) => ({
					...prevState,
					currentProfile: response.profile,
					courses: response.courses,
					loading: false,
				}));
			} catch (error) {
				setState((prevState) => ({
					...prevState,
					loading: false,
				}));
			}
		};

		getProfile();
	}, [profileSlug]);

	let splittedCourses: TSplittedCourses = categories.map((category) => ({
		category: category.name,
		courses: [],
	}));

	for (let i = 0; i < state.courses.length; i++) {
		const index = categories.findIndex(
			(category) => state.courses[i].category === category.name,
		);
		if (index === -1) {
			continue;
		}

		splittedCourses[index].courses.push(state.courses[i]);
	}

	splittedCourses = splittedCourses.filter(
		(section) => section.courses.length > 0,
	);

	if (!state.loading && !state.currentProfile) {
		return <NotFound white text="User not found" />;
	}

	if (state.loading) {
		return (
			<Container
				navbarOrkaProps={{ white: true }}
				className="public-profile-container">
				<div style={{ display: "flex", justifyContent: "center" }}>
					<Loading absolute={false} />
				</div>
			</Container>
		);
	}

	if (!state.currentProfile) {
		return null;
	}

	return (
		<Container
			navbarOrkaProps={{ white: true }}
			className="public-profile-container">
			<div className="spacing" />
			<Grid2 container spacing={{ xs: 2, sm: 2, md: 2, lg: 4, xl: 4 }}>
				<Grid2 size={{ xs: "auto" }} className="profile-left-column">
					{state.currentProfile.url && (
						<img
							src={state.currentProfile.thumbnail || state.currentProfile.url}
							className="avatar"
							onClick={() => {
								imageViewerRef.current?.open({
									url: state.currentProfile?.url ?? "",
								});
							}}
						/>
					)}
					{!state.currentProfile.url && (
						<img src={noAvatarImage} className="avatar" />
					)}
					{/* <div className="verification-container">
						<Typography className="big-text bold">Verification</Typography>
						{state.currentProfile.identity && (
							<div className="verification-item">
								<FaCheck className="normal-icon check-icon" />
								<Typography className="normal-text">Identity</Typography>
							</div>
						)}
						{state.currentProfile.certificates && (
							<div className="verification-item">
								<FaCheck className="normal-icon check-icon" />
								<Typography className="normal-text">
									Airman Certificates
								</Typography>
							</div>
						)}
						{!state.currentProfile.identity &&
							!state.currentProfile.certificates && (
								<div className="verification-item">
									<Typography className="normal-text">Pending</Typography>
								</div>
							)}
					</div> */}
				</Grid2>
				<Grid2
					className="profile-right-column"
					width={{
						xs: "100%",
						sm: "calc(100% - 150px - 16px)",
						md: "calc(100% - 250px - 16px)",
						lg: "calc(100% - 250px - 32px)",
						xl: "calc(100% - 250px - 32px)",
					}}>
					<Typography>
						<Typography className="small-title-text bold" component="span">
							{`${state.currentProfile.user?.firstName ?? ""} ${state.currentProfile.user?.lastName ?? ""}`.trim()}
						</Typography>
						{state.currentProfile.nickName && (
							<Typography className="normal-text" component="span">
								{` (${state.currentProfile.nickName})`}
							</Typography>
						)}
					</Typography>
					{state.currentProfile.languages && (
						<Typography className="normal-text">
							{`Fluent: ${state.currentProfile.languages}`}
						</Typography>
					)}
					{state.currentProfile.introduction && (
						<RichTypography
							id="intro"
							className="normal-text"
							textAlign="justify"
							whiteSpace="break-spaces"
							text={state.currentProfile.introduction}
						/>
					)}
					{!state.currentProfile.introduction && (
						<Typography
							className="normal-text"
							textAlign="justify"
							whiteSpace="break-spaces">
							No intro yet.
						</Typography>
					)}
				</Grid2>
			</Grid2>
			<Typography className="big-text bold section-title">
				How Can I Help You?
			</Typography>
			{state.courses.length > 0 && (
				<CourseList splittedCourses={splittedCourses} className="course-list" />
			)}
			{state.courses.length === 0 && (
				<Typography className="normal-text course-list" textAlign="center">
					No public services yet.
				</Typography>
			)}

			{account?.userId !== state.currentProfile.user?.userId && (
				<>
					<div className="spacing" />
					<Typography className="big-text bold">
						Still have questions?
					</Typography>
					<Link to={`/messages/${state.currentProfile.user?.userId}`}>
						<Button className="normal-button black-button message-instructor-button">
							Message Me
						</Button>
					</Link>
				</>
			)}

			<ImageViewer ref={imageViewerRef} />
			<VideoViewer ref={videoViewerRef} />
		</Container>
	);
}
